import { useEffect, useState } from 'react';
import axios from 'axios';
import { useModalOpen } from '../../../hooks/useToastModalOpen';
import { useShallow } from 'zustand/shallow';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';
import useMenuPermission from '../../../hooks/useMenuPermission';

const UserDetail = ({detailData, selectUser, setWithdrawalUserOpen, setReSearch, updateUserRefresh, selectEmog, setSelectEmog, updateEmog, setUpdateEmog, setJoinUserOpen}) => {
    
    // localStorage에서 원하는 계정 정보 map으로 가져오기
    const keys = ['id'];
    const localValues = Object.fromEntries( keys.map(key => [key, localStorage.getItem(key)]) );
    
    const notSelectUser = selectUser.useridno === '';
    
    // 기관 검색 modal
    const [emogSearchLayerOpen, setEmogSearchLayerOpen] = useModalStore(useShallow((state) => [state.emogSearchLayerOpen, state.setEmogSearchLayerOpen]));

    const userDetail = detailData?.userDetail;
    const authName = detailData?.authName;
    const authList = detailData?.authList;
    const roleName = detailData?.roleName;
    // 등급 · 롤 · 관리자 여부 · 관리 메뉴 권한을 고를 수 있는지는 서버가 정해 내려 준다
    // privilegedEditable: J001 관리자 · 시스템 관리자 / roleEditable: 그중 이 사용자의 등급 · 롤을 바꿀 수 있는가(102 · 110 사용자는 시스템 관리자만)
    const privilegedEditable = detailData?.privilegedEditable === true;
    const roleEditable = detailData?.roleEditable === true;
    // 사용자관리 메뉴 권한(롤 권한 + 개인 권한). 버튼은 그대로 두고, 수정 · 삭제 권한이 없으면 누를 때 안내한다(서버도 같은 기준으로 막는다)
    const { withPermission } = useMenuPermission();
    const roleList = detailData?.roleList;
    
    const [modifyParams, setModifyParams] = useState({
        useridno: selectUser.useridno,
        useremcd: '',
        emogdesc: '',
        userauth: '',
        roleno: '',
        userprjt: 'N',
        userstrm: 'N',
        useramyn: 'N', // 승인여부
        useradmn: 'N',
        admnUserMng: 'N',
        admnAuditLog: 'N',
        admnExceldownLog: 'N',
        useretc: '',
    });
    
    // 수정할 사용자 정보 set
    const paramsList = ['useridno', 'useremcd', 'emogdesc', 'userauth', 'roleno', 'useradmn', 'useramyn', 'userprjt', 'userstrm', 'useretc', 'admnUserMng', 'admnAuditLog', 'admnExceldownLog'];
    const setParams = paramsList.reduce((acc,key) => {
        if(userDetail !== undefined) {
            if(userDetail[key] !== null) {
                acc[key] = userDetail[key];
                return acc;
            } else {
                acc[key] = '';
                return acc;
            }
        }
    }, {})

    // toast modal open function
    const showNotiModal = useModalStore(state => state.showNotiModal);

    // confirm hook
    const confirm = useConfirm();

    // toast modal open function
    const { toastModalOpen } = useModalOpen();
    
    useEffect(() => {
        if ((userDetail !== undefined)) {
            if(!updateEmog) {
                const updatedParams = paramsList.reduce((acc, key) => {
                    acc[key] = setParams[key];
                    return acc;
                }, {});
                
                if(!emogSearchLayerOpen) {
                    // 기관 검색 모달이 닫혀있을 때만 업데이트(필요없는 렌더링 방지)
                    setModifyParams((prev) => ({
                        ...prev,
                        ...updatedParams,
                    }));
                }
            } else if(updateEmog) {
                // 기관 선택 시 기관 정보만 업데이트
                setModifyParams((prev) => ({
                    ...prev,
                    useremcd: selectEmog.emogcode,
                    emogdesc: selectEmog.emogdesc,
                }));
            }
        }
    }, [detailData, updateEmog, selectUser]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newParams = {
            ...modifyParams,
            [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : name === 'roleno' ? Number(value) : value
        };
        setModifyParams(newParams);
    };

    // 사용자 승인/미승인 처리
    const userUpdateAmyn = async (modifyParams) => {
        try {
            if(notSelectUser) {
                showNotiModal('사용자를 선택해주세요.');
                return false;
            }

            const ok = await confirm(`${modifyParams.useramyn === 'N' ? '승인' : '미승인'} 처리 하시겠습니까?`);
            if(!ok) return false;

            await axios.post('/commonapi/updateOkay.do', {
                useridno: modifyParams.useridno,
                useramyn: modifyParams.useramyn,
            });
            toastModalOpen(`${modifyParams.useramyn === 'N' ? '승인' : '미승인'} 처리 되었습니다.`);
            setReSearch(true);
            updateUserRefresh(modifyParams.useridno);

        } catch (error) {
            console.error('승인/미승인 처리 중 오류 발생:', error);
        }
    }
    
    // 사용자 아이핀 초기화
    const useripinReset = async (useridno) => {
        try {
            const ok = await confirm('사용자 IPIN 정보 및 OTP 인증 정보를 초기화 하시겠습니까?');
            if(!ok) return false;

            await axios.post('/commonapi/useripinReset.do', {
                useridno: useridno,
                userupid: localValues.id,
            });
            toastModalOpen('정상적으로 초기화 되었습니다.');
            setReSearch(true);
            updateUserRefresh(useridno);
            
        } catch (error) {
            console.error('사용자 IPIN 정보 및 OTP 인증 정보 초기화 중 오류 발생:', error);
        }
    }

    // 사용자 비밀번호 초기화
    const passwordReset = async (useridno) => {
        try {
            const ok = await confirm('사용자 비밀번호를 초기화 하시겠습니까?');
            if(!ok) return false;

            await axios.post('/commonapi/passwordReset.do', {
                useridno: useridno,
                Resetuserpwdyn: 'Y'
            });
            toastModalOpen('정상적으로 초기화 되었습니다.');
            setReSearch(true);
            updateUserRefresh(useridno);
        
        } catch (error) {
            console.error('사용자 비밀번호 초기화 중 오류 발생:', error);
        }
    }

    // 고른 등급의 롤 선택지. 사용 안 하는 롤은 지금 그 롤을 가진 사용자일 때만 보여 준다
    const roleOptionsOf = (userauth) => (roleList ?? []).filter(role =>
        role.AUTHJOBS === userauth && (role.USEYN === 'Y' || Number(role.ROLENO) === Number(userDetail?.roleno))
    );
    const roleOptions = roleOptionsOf(modifyParams.userauth);

    useEffect(() => {
        const currentAuth = modifyParams.userauth;
        // 등급을 바꾸지 않았으면 사용자의 지금 롤을 그대로 둔다(모든 등급).
        // 여기서 등급 기본 롤로 바꾸면 J001 의 102 · 103 · 104 나 J003 의 302 사용자가
        // 다른 항목만 고쳐 저장해도 기본 롤로 내려간다.
        // 다른 등급으로 바꿨다가 원래 등급으로 되돌리면 원래 롤로 되돌린다.
        if (currentAuth === userDetail?.userauth && userDetail?.roleno != null) {
            if (modifyParams.roleno !== userDetail.roleno) {
                setModifyParams(prev => ({ ...prev, roleno: userDetail.roleno }));
            }
            return;
        }
        // 등급을 바꾸면(또는 롤이 비어 있으면) 그 등급의 기본 롤(DEFAULTYN)로, 없으면 첫 롤로 채운다
        const options = roleOptionsOf(currentAuth).filter(role => role.USEYN === 'Y');
        const defaultRole = options.find(role => role.DEFAULTYN === 'Y') ?? options[0];
        const mappedRole = defaultRole ? Number(defaultRole.ROLENO) : '';

        // 이미 동일한 role이면 set 생략
        if (modifyParams.roleno !== mappedRole) {
            setModifyParams(prev => ({ ...prev, roleno: mappedRole }));
        }
    }, [modifyParams.userauth]);
    
    // 사용자 정보 수정
    const userUpdate = async (modifyParams) => {
        try {
            if(notSelectUser) {
                showNotiModal('사용자를 선택해주세요.');
                return false;
            }
            const modiList = paramsList.filter(item => item !== 'useridno' && item !== 'useretc');
            const isSame = modiList.every((key) => modifyParams[key] === userDetail[key])
            const etcSame = userDetail.useretc === null ? modifyParams.useretc === '' : modifyParams.useretc === userDetail.useretc;
            
            if (isSame && etcSame) {
                showNotiModal('변경사항이 없습니다.');
                return false;
            }

            if (roleEditable && !roleOptions.some(role => Number(role.ROLENO) === Number(modifyParams.roleno))) {
                showNotiModal('선택한 등급에서 쓸 수 있는 롤이 없습니다.');
                return false;
            }

            const ok = await confirm('사용자 정보를 수정하시겠습니까?');
            if(!ok) return false;

            // payload 구성 (API에 넘겨줄 데이터 중복 세팅 및 가공)
            const keyList = ['useradmn', 'useramyn', 'userprjt', 'userstrm', 'useretc', 'admnUserMng', 'admnAuditLog', 'admnExceldownLog']
            const payload = keyList.reduce((acc, key) => {
                acc[key] = modifyParams[key] === userDetail[key] ? userDetail[key] : modifyParams[key];
                return acc;
            }, {
                userupid: localValues.id,
                useridno: modifyParams.useridno,
                useremcd: modifyParams.useremcd,
                userauth: modifyParams.userauth,
                roleno: Number(modifyParams.roleno),
            });

            const response = await axios.post('/commonapi/updateUserInfo.do', payload);
            const result = response.data.resultMap.result;
            const resultMessage = response.data.resultMap.resultMessage;

            if(result) {
                toastModalOpen(resultMessage);
                setReSearch(true);
                // 범위 밖 기관으로 옮긴 사용자는 더 볼 수 없으므로 상세를 다시 부르지 않고 선택을 푼다
                updateUserRefresh(response.data.resultMap.movedOutOfScope ? '' : modifyParams.useridno);
            } else {
                showNotiModal(resultMessage);
                if(resultMessage.includes('관리자')) {
                    setModifyParams((prev) => ({
                        ...prev,
                        useradmn: 'N',
                    }));
                }
            }
        
        } catch (error) {
            console.error('사용자 정보 수정 중 오류 발생:', error);
        }
    };
    
    const onClickOpen = () => {
        setEmogSearchLayerOpen(true);
        setSelectEmog({
            emogcode: '',
            emogdesc: ''
        })
        setUpdateEmog(false);
    }

    return (
        <div id="detailform" >
            <div className="com-search">
                <table>
                    <colgroup>
                        <col width="10%" />
                        <col width="23%" />
                        <col width="11%" />
                        <col width="23%" />
                        <col width="10%" />
                        <col width="23%" />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>사용자ID</th>
                            <td>
                                <span name="useridno">{userDetail?.useridno}</span>
                            </td>
                            <th>소속기관</th>
                            <td colSpan="3">
                                <div className='flex gap-2'>
                                    <input id='w-half' className='input_style' type="text" name="useremcd" value={modifyParams?.emogdesc} maxLength="55" disabled />
                                    {notSelectUser ? '' :
                                        <>
                                            <button onClick={withPermission('UPYN', onClickOpen)} className='pw_btn bg_ho_primary' title='기관검색'>&nbsp;기관검색</button>
                                            {/* <button onClick={onClickOpen} className='pw_btn bg_st_primary' title='기관정보'>&nbsp;기관정보</button> */}
                                        </>
                                    }
                                </div>
                            </td>

                        </tr>
                        <tr>
                            <th>사용자명</th>
                            <td>
                                <span>{userDetail?.username}</span>
                            </td>
                            <th>사용자등급</th>
                            <td>
                                {notSelectUser ? '' :
                                !roleEditable ? `${authName ?? ''}(${userDetail?.userauth ?? ''})${roleName ? ` / ${roleName}(${userDetail?.roleno})` : ''}` :
                                <div className='flex gap-2'>
                                    <select name="userauth" className='w-half' value={modifyParams?.userauth} onChange={handleInputChange}>
                                        <option>사용자등급선택</option>
                                        {authList?.map((item, idx) => {
                                            return (
                                                <option key={idx} value={item.bscdminr}>
                                                    {item.bscddesc}
                                                </option>
                                            )
                                        })}
                                    </select>
                                    <select name="roleno" className='w-half' value={modifyParams?.roleno} onChange={handleInputChange}>
                                        {roleOptions.length === 0 ? <option value=''>롤 없음</option> : ''}
                                        {roleOptions.map(role => (
                                            <option key={role.ROLENO} value={role.ROLENO}>
                                                {role.ROLENAME}({role.ROLENO}){role.USEYN === 'Y' ? '' : ' 사용 안 함'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                }
                            </td>
                            <th>관리자권한</th>
                            <td>
                                {notSelectUser ? '' : !privilegedEditable ? userDetail?.useradmn :
                                    <select className="w-full" name="useradmn" value={modifyParams?.useradmn} onChange={handleInputChange}>
                                        <option value='Y'>Y</option>
                                        <option value='N'>N</option>
                                    </select>
                                }
                            </td>
                        </tr>
                        <tr>
                            <th>접근권한</th>
                            <td>
                                <div className='flex gap-2'>
                                    <label htmlFor="userprjt">
                                        <input type="checkbox" id='userprjt' name="userprjt" 
                                            checked={modifyParams?.userprjt === 'Y'} 
                                            onChange={handleInputChange} 
                                            disabled={notSelectUser ? 'disabled' : ''}
                                        />
                                        표준 등록체계
                                    </label>
                                    <label htmlFor="userstrm">
                                        <input type="checkbox" id='userstrm' name="userstrm" 
                                            checked={modifyParams?.userstrm === 'Y'} 
                                            onChange={handleInputChange} 
                                            disabled={notSelectUser ? 'disabled' : ''}
                                        />
                                        외상 등록체계
                                    </label>
                                </div>
                            </td>
                            <th>사용 시작/종료일</th>
                            <td>
                                <span name="userdt">
                                    {userDetail?.userfrdt === undefined || userDetail?.userfrdt > '9999-12-30' ? '' :
                                        userDetail?.userfrdt + ' ~ ' + userDetail?.usertodt
                                    }
                                </span>
                            </td>
                            <th><label htmlFor="useretc">비고</label></th>
                            <td>
                                {notSelectUser ? '' : 
                                <input type="text" className='input_style' name="useretc" value={modifyParams?.useretc} maxLength="11" onChange={handleInputChange} />
                                }
                            </td>
                        </tr>
                        <tr>
                            <th><label htmlFor="userpswd">비밀번호</label></th>
                            <td colSpan={notSelectUser || !privilegedEditable ? 5 : 3}>
                                <div className='flex gap-2 items-center'>
                                    {/* <input type="password" className='input_style' name="userpswd" maxLength="20" onChange={handleInputChange} disabled={notSelectUser ? 'disabled' : ''} /> */}
                                    {!notSelectUser &&
                                        <>
                                            <button onClick={withPermission('UPYN', () => passwordReset(userDetail?.useridno))} className='pw_btn bg_ho_primary' title='비밀번호 초기화'>&nbsp;비밀번호 초기화</button>
                                            <span className='co_red'>※ 비밀번호 초기화시 임의의 8자리 숫자로 초기화</span>
                                        </>
                                    }
                                    {notSelectUser || userDetail?.userpswdtmp === null ? ''
                                        :
                                        <span className='font-bold'>※ 임시 비밀번호 : <span className='co_red'>{userDetail?.userpswdtmp}</span></span>
                                    }
                                </div>
                            </td>
                            {notSelectUser || !privilegedEditable ? '' : 
                            <>
                                <th>인가권한</th>
                                <td colSpan={5}>
                                    <div className='flex gap-2'>
                                        <label htmlFor="admnUserMng">
                                            <input type="checkbox" id='admnUserMng' name="admnUserMng" 
                                                checked={modifyParams?.admnUserMng === 'Y'} 
                                                onChange={handleInputChange} 
                                                disabled={notSelectUser ? 'disabled' : ''}
                                            />
                                            사용자 관리
                                        </label>
                                        <label htmlFor="admnAuditLog">
                                            <input type="checkbox" id='admnAuditLog' name="admnAuditLog" 
                                                checked={modifyParams?.admnAuditLog === 'Y'} 
                                                onChange={handleInputChange} 
                                                disabled={notSelectUser ? 'disabled' : ''}
                                            />
                                            감사로그
                                        </label>
                                        <label htmlFor="admnExceldownLog">
                                            <input type="checkbox" id='admnExceldownLog' name="admnExceldownLog" 
                                                checked={modifyParams?.admnExceldownLog === 'Y'} 
                                                onChange={handleInputChange} 
                                                disabled={notSelectUser ? 'disabled' : ''}
                                            />
                                            엑셀 다운로드 로그
                                        </label>
                                    </div>
                                </td>
                            </>
                            }
                        </tr>
                        <tr>
                            <th>사용승인여부</th>
                            <td>
                                {notSelectUser ? '' : 
                                <span name="useramyn">{userDetail?.useramyn === 'Y' ? '승인' : '미승인'}</span>
                                }
                            </td>
                            <th>인증정보</th>
                            <td>
                                {notSelectUser ? '' : userDetail?.authYn === null ? '' :
                                    <span name='authYn'>{userDetail?.authYn === 'Y' ? 'IPIN 인증완료' : 'IPIN 미인증'}</span>
                                }
                            </td>
                            <th>USERIPIN</th>
                            <td colSpan="5">
                                <div className='flex gap-2'>
                                    {notSelectUser ? '' : userDetail?.useripin === undefined ? '없음' : 
                                        <span name="useripin">{userDetail?.useripin?.length > 24 ? userDetail?.useripin?.substring(0, 22) + '....' : userDetail?.useripin}</span>
                                    }
                                    {userDetail?.useripin === undefined || userDetail?.useripin === null ? '' :
                                        <button onClick={withPermission('UPYN', () => useripinReset(userDetail?.useridno))} className='pw_btn' title='USER IPIN 초기화'>&nbsp;초기화</button>
                                    }
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>로그인횟수</th>
                            <td>
                                {notSelectUser ? '' : <span name="useraccn">{userDetail?.useraccn ? userDetail?.useraccn : '0'}회</span>}
                            </td>
                            <th>최종로그인일시</th>
                            <td>
                                {notSelectUser ? '' : 
                                <span name="useracdt">{userDetail?.useracdt === '9999-12-31 23:59:59' || userDetail?.useracdt === undefined ? '로그인정보없음' : userDetail?.useracdt}</span>
                                }
                            </td>
                            <th>최종수정일시</th>
                            <td><span name="userupdt">{userDetail?.userupdt}</span></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className='flex gap-2 py-2 justify-self-end'>
                <button onClick={withPermission('UPYN', () => userUpdateAmyn(modifyParams))} className='search_btn bg_st_primary' title='사용자 승인/미승인'>{userDetail?.useramyn === 'Y' ? '미승인' : '승인'}</button>
                <button onClick={withPermission('UPYN', () => userUpdate(modifyParams))} className='search_btn bg_ho_primary' title='사용자 정보 수정'>수정</button>
                {/* <button onClick={() => setJoinUserOpen(true)} className='search_btn bg_ho_primary' title='등록'>등록</button> */}
                <button onClick={withPermission('DLYN', () => {notSelectUser ? showNotiModal('사용자를 선택해주세요.') : setWithdrawalUserOpen(true)})} className='search_btn bg_tr_primary' title='사용자 탈퇴'>탈퇴</button>
            </div>
        </div>
    )
}

export default UserDetail